<div class="profile-shell" [class.faculty-shell]="isFaculty">
  @if (isFaculty) {
    <app-counselor-nav></app-counselor-nav>
  } @else {
    <app-student-nav></app-student-nav>
  }

  <div class="page">
    <!-- MAIN COLUMN -->
    <div class="main-col">
      <!-- HERO CARD -->
      <mat-card class="hero-card" data-testid="hero-card">
        <div class="hero-banner" aria-hidden="true"></div>
        <mat-card-content class="hero-body">
          <div
            class="hero-avatar"
            data-testid="hero-avatar-btn"
            (click)="openModal('avatar')"
            role="button"
            tabindex="0"
            aria-label="Edit your avatar"
            (keydown.enter)="openModal('avatar')"
            (keydown.space)="openModal('avatar')"
          >
            <app-user-avatar
              [imageUrl]="
                profile?.profile_picture_path
                  ? profile!.profile_picture_path! + '?t=' + avatarTimestamp
                  : null
              "
              [name]="fullName"
            />
            <div class="hero-avatar-overlay" aria-hidden="true">
              <mat-icon>photo_camera</mat-icon>
            </div>
          </div>
          <div class="hero-info">
            <div class="hero-name" data-testid="hero-name">{{ fullName }}</div>
            @if (isStudent && profile?.career_id) {
              <div class="hero-career" data-testid="hero-career">
                Future {{ getCareerLabel(profile?.career_id ?? null) }}
              </div>
            }
            @if (isStudent) {
              <div class="hero-meta">
                @if (gradeLabel || schoolName) {
                  <span class="hero-meta-item" data-testid="hero-meta-school">
                    <mat-icon class="meta-icon" aria-hidden="true">school</mat-icon>
                    {{ gradeLabel }}{{ gradeLabel && schoolName ? ' · ' : '' }}{{ schoolName }}
                  </span>
                }
              </div>
            }
            <div class="hero-actions">
              <button
                mat-flat-button
                class="btn-primary"
                data-testid="edit-profile-btn"
                (click)="openModal('basic')"
                aria-label="Edit your profile"
              >
                Edit profile
              </button>
              <button
                mat-stroked-button
                class="btn-outline"
                data-testid="share-profile-btn"
                aria-label="Share your profile"
              >
                Share profile
              </button>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ABOUT -->
      <mat-card class="section-card" data-testid="about-card">
        <mat-card-header class="section-header">
          <mat-card-title class="section-title">About</mat-card-title>
          <button
            mat-flat-button
            class="section-edit"
            data-testid="edit-about-btn"
            (click)="openModal('about')"
            aria-label="Edit your bio"
          >
            <mat-icon aria-hidden="true">edit</mat-icon> Edit
          </button>
        </mat-card-header>
        <mat-divider></mat-divider>
        <mat-card-content class="section-body">
          @if (profile?.bio) {
            <p class="about-text" data-testid="about-text">{{ profile?.bio }}</p>
          } @else {
            <p class="about-empty" data-testid="about-empty">
              Add a short bio to tell your story — who you are, what drives you, and where you're
              headed.
            </p>
          }
        </mat-card-content>
      </mat-card>

      <!-- DETAILS -->
      <mat-card class="section-card" data-testid="details-card">
        <mat-card-header class="section-header">
          <mat-card-title class="section-title">Profile details</mat-card-title>
          @if (isStudent) {
            <button
              mat-flat-button
              class="section-edit"
              data-testid="edit-details-btn"
              (click)="openModal('details')"
              aria-label="Edit profile details"
            >
              <mat-icon aria-hidden="true">edit</mat-icon> Edit
            </button>
          }
        </mat-card-header>
        <mat-divider></mat-divider>
        <mat-card-content class="section-body">
          <div class="details-grid">
            <div class="detail-item">
              <div class="detail-label" id="label-preferred">Preferred name</div>
              @if (currentUser?.preferredName) {
                <div
                  class="detail-value"
                  data-testid="detail-preferred-name"
                  aria-labelledby="label-preferred"
                >
                  {{ currentUser?.preferredName }}
                </div>
              } @else {
                <div
                  class="detail-empty"
                  data-testid="detail-preferred-name-empty"
                  aria-labelledby="label-preferred"
                >
                  Same as first name
                </div>
              }
            </div>
            @if (isStudent) {
              <div class="detail-item">
                <div class="detail-label" id="label-grade">Grade</div>
                @if (gradeLabel) {
                  <div
                    class="detail-value"
                    data-testid="detail-grade"
                    aria-labelledby="label-grade"
                  >
                    {{ gradeLabel }}
                  </div>
                } @else {
                  <div
                    class="detail-empty"
                    data-testid="detail-grade-empty"
                    aria-labelledby="label-grade"
                  >
                    Not set
                  </div>
                }
              </div>
              <div class="detail-item">
                <div class="detail-label" id="label-school">School</div>
                @if (schoolName) {
                  <div
                    class="detail-value"
                    data-testid="detail-school"
                    aria-labelledby="label-school"
                  >
                    {{ schoolName }}
                  </div>
                } @else {
                  <div
                    class="detail-empty"
                    data-testid="detail-school-empty"
                    aria-labelledby="label-school"
                  >
                    Not set
                  </div>
                }
              </div>
            }
            @if (isStudent) {
              <div class="detail-item">
                <div class="detail-label" id="label-career">Career goal</div>
                @if (profile?.career_id) {
                  <div
                    class="detail-value"
                    data-testid="detail-career"
                    aria-labelledby="label-career"
                  >
                    {{ getCareerLabel(profile?.career_id ?? null) }}
                  </div>
                } @else {
                  <div
                    class="detail-empty"
                    data-testid="detail-career-empty"
                    aria-labelledby="label-career"
                  >
                    Not set
                  </div>
                }
              </div>
            }
            <div class="detail-item">
              <div class="detail-label" id="label-email">Email</div>
              @if (currentUser?.email) {
                <div class="detail-value" data-testid="detail-email" aria-labelledby="label-email">
                  {{ currentUser?.email }}
                </div>
              }
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- INTERESTS -->
      <mat-card class="section-card" data-testid="interests-card">
        <mat-card-header class="section-header">
          <mat-card-title class="section-title">Hobbies & Interests</mat-card-title>
          <button
            mat-flat-button
            class="section-edit"
            data-testid="edit-interests-btn"
            (click)="openModal('interests')"
            aria-label="Edit your hobbies and interests"
          >
            <mat-icon aria-hidden="true">edit</mat-icon> Edit
          </button>
        </mat-card-header>
        <mat-divider></mat-divider>
        <mat-card-content class="section-body">
          @if (interestsList.length > 0) {
            <mat-chip-set
              class="interests-grid"
              data-testid="interests-list"
              aria-label="Your interests"
            >
              @for (interest of interestsList; track interest) {
                <mat-chip
                  class="interest-chip"
                  [attr.data-testid]="
                    'interest-chip-' + interest.name.toLowerCase().replace(' ', '-')
                  "
                  >{{ interest.name }}</mat-chip
                >
              }
            </mat-chip-set>
          } @else {
            <p class="about-empty" data-testid="interests-empty">No interests added yet.</p>
          }
        </mat-card-content>
      </mat-card>
    </div>

    <!-- SIDE COLUMN -->
    <div class="side-col">
      <!-- AVATAR CARD -->
      <mat-card class="avatar-card" data-testid="avatar-card">
        <mat-card-header class="avatar-card-header">
          <mat-card-title>My avatar</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          @if (profile?.profile_picture_path) {
            <div class="avatar-snapshot-wrap" data-testid="avatar-image">
              <app-user-avatar
                [imageUrl]="profile!.profile_picture_path! + '?t=' + avatarTimestamp"
                [name]="fullName"
              />
            </div>
          } @else {
            <div
              class="avatar-placeholder"
              data-testid="avatar-placeholder"
              aria-label="No avatar set"
            >
              <mat-icon class="avatar-placeholder-icon" aria-hidden="true">person</mat-icon>
              <div class="avatar-placeholder-text">Build your avatar — choose your look!</div>
            </div>
          }
        </mat-card-content>
        <mat-card-actions class="avatar-card-actions">
          <button
            mat-flat-button
            class="btn-primary full"
            data-testid="avatar-action-btn"
            (click)="openModal('avatar')"
            [attr.aria-label]="avatar ? 'Edit your avatar' : 'Create your avatar'"
          >
            {{ avatar ? 'Edit avatar' : '+ Create avatar' }}
          </button>
        </mat-card-actions>
      </mat-card>

      <!-- PROFILE STRENGTH -->
      <mat-card class="completion-card" data-testid="completion-card">
        <mat-card-content>
          <div class="completion-header">
            <div class="completion-title" aria-hidden="true">Profile strength</div>
            <div class="completion-pct" data-testid="completion-percent" aria-hidden="true">
              {{ completionPercent }}%
            </div>
          </div>
          <mat-progress-bar
            class="completion-progress"
            data-testid="completion-progress-bar"
            mode="determinate"
            [value]="completionPercent"
            [attr.aria-valuenow]="completionPercent"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-label]="'Profile strength ' + completionPercent + '% complete'"
          >
          </mat-progress-bar>
          <div
            class="completion-items"
            data-testid="completion-items"
            role="list"
            aria-label="Profile completion checklist"
          >
            @for (item of completionItems; track item.label) {
              <div
                class="completion-item"
                [attr.data-testid]="'completion-item-' + item.label.toLowerCase().replace(' ', '-')"
                role="listitem"
                [attr.aria-label]="item.label + (item.done ? ' — complete' : ' — incomplete')"
              >
                <mat-icon
                  class="completion-icon"
                  [class.done]="item.done"
                  [class.missing]="!item.done"
                  aria-hidden="true"
                >
                  {{ item.done ? 'check_circle' : 'radio_button_unchecked' }}
                </mat-icon>
                {{ item.label }}
              </div>
            }
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- ── DIALOG TEMPLATES ─────────────────── -->

  <!-- Basic info dialog -->
  <ng-template #basicDialog>
    <h2 mat-dialog-title class="dialog-title">Edit basic info</h2>
    <mat-dialog-content class="dialog-content">
      <mat-form-field appearance="outline" class="dialog-field">
        <mat-label>First name</mat-label>
        <input
          matInput
          data-testid="basic-first-name-input"
          [(ngModel)]="editBasic.firstName"
          [disabled]="true"
          aria-label="First name"
        />
      </mat-form-field>
      <mat-form-field appearance="outline" class="dialog-field">
        <mat-label>Last name</mat-label>
        <input
          matInput
          data-testid="basic-last-name-input"
          [(ngModel)]="editBasic.lastName"
          [disabled]="true"
          aria-label="Last name"
        />
      </mat-form-field>
      <mat-form-field appearance="outline" class="dialog-field">
        <mat-label>Preferred name</mat-label>
        <input
          matInput
          data-testid="basic-preferred-name-input"
          [(ngModel)]="editBasic.preferredName"
          aria-label="Preferred name"
        />
        <mat-hint>Optional. If set, this is shown instead of your first name.</mat-hint>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions class="dialog-actions">
      <button
        mat-stroked-button
        class="btn-outline"
        data-testid="basic-cancel-btn"
        mat-dialog-close
        aria-label="Cancel editing basic info"
      >
        Cancel
      </button>
      <button
        mat-flat-button
        class="btn-primary"
        data-testid="basic-save-btn"
        (click)="save()"
        [disabled]="isSaving"
        [attr.aria-label]="isSaving ? 'Saving changes' : 'Save changes'"
      >
        {{ isSaving ? 'Saving...' : 'Save changes' }}
      </button>
    </mat-dialog-actions>
  </ng-template>

  <!-- About dialog -->
  <ng-template #aboutDialog>
    <h2 mat-dialog-title class="dialog-title">Edit bio</h2>
    <mat-dialog-content class="dialog-content">
      <mat-form-field appearance="outline" class="dialog-field">
        <mat-label>Bio</mat-label>
        <textarea
          matInput
          data-testid="about-bio-input"
          [(ngModel)]="editAbout.bio"
          rows="5"
          placeholder="Tell your story..."
          aria-label="Bio"
        ></textarea>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions class="dialog-actions">
      <button
        mat-stroked-button
        class="btn-outline"
        data-testid="about-cancel-btn"
        mat-dialog-close
        aria-label="Cancel editing bio"
      >
        Cancel
      </button>
      <button
        mat-flat-button
        class="btn-primary"
        data-testid="about-save-btn"
        (click)="save()"
        [disabled]="isSaving"
        [attr.aria-label]="isSaving ? 'Saving changes' : 'Save changes'"
      >
        {{ isSaving ? 'Saving...' : 'Save changes' }}
      </button>
    </mat-dialog-actions>
  </ng-template>

  <!-- Details dialog -->
  <ng-template #detailsDialog>
    <h2 mat-dialog-title class="dialog-title">Edit profile details</h2>
    <mat-dialog-content class="dialog-content">
      @if (isStudent) {
        <mat-form-field appearance="outline" class="dialog-field">
          <mat-label>Grade level</mat-label>
          <mat-select
            data-testid="details-grade-select"
            [(ngModel)]="editDetails.grade_level"
            aria-label="Select grade level"
            [disabled]="true"
          >
            @for (g of gradeOptions; track g.value) {
              <mat-option [value]="g.value">{{ g.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="dialog-field">
          <mat-label>Career goal</mat-label>
          <mat-select
            data-testid="details-career-select"
            [(ngModel)]="editDetails.career_id"
            aria-label="Select career goal"
          >
            <mat-option [value]="null">Select a career...</mat-option>
            @for (c of careers; track c.id) {
              <mat-option [value]="c.id">{{ c.icon }} {{ c.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      }
    </mat-dialog-content>
    <mat-dialog-actions class="dialog-actions">
      <button
        mat-stroked-button
        class="btn-outline"
        data-testid="details-cancel-btn"
        mat-dialog-close
        aria-label="Cancel editing profile details"
      >
        Cancel
      </button>
      <button
        mat-flat-button
        class="btn-primary"
        data-testid="details-save-btn"
        (click)="save()"
        [disabled]="isSaving"
        [attr.aria-label]="isSaving ? 'Saving changes' : 'Save changes'"
      >
        {{ isSaving ? 'Saving...' : 'Save changes' }}
      </button>
    </mat-dialog-actions>
  </ng-template>

  <!-- Interests dialog -->
  <ng-template #interestsDialog>
    <h2 mat-dialog-title class="dialog-title">Edit interests</h2>
    <mat-dialog-content class="dialog-content">
      <div class="dialog-label" id="interests-dialog-label">Select your interests</div>
      <div
        class="modal-interests-grid"
        data-testid="interests-options-grid"
        role="group"
        aria-labelledby="interests-dialog-label"
      >
        @for (interest of interestOptions; track interest) {
          <button
            mat-flat-button
            class="interest-btn"
            [attr.data-testid]="'interest-option-' + interest.name.toLowerCase().replace(' ', '-')"
            [class.selected]="isInterestSelected(interest)"
            (click)="toggleModalInterest(interest)"
            [attr.aria-pressed]="isInterestSelected(interest)"
            [attr.aria-label]="interest.name + (isInterestSelected(interest) ? ' — selected' : '')"
          >
            {{ interest.name }}
          </button>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions class="dialog-actions">
      <button
        mat-stroked-button
        class="btn-outline"
        data-testid="interests-cancel-btn"
        mat-dialog-close
        aria-label="Cancel editing interests"
      >
        Cancel
      </button>
      <button
        mat-flat-button
        class="btn-primary"
        data-testid="interests-save-btn"
        (click)="save()"
        [disabled]="isSaving"
        [attr.aria-label]="isSaving ? 'Saving changes' : 'Save changes'"
      >
        {{ isSaving ? 'Saving...' : 'Save changes' }}
      </button>
    </mat-dialog-actions>
  </ng-template>

  <!-- Avatar dialog -->
  <ng-template #avatarDialog>
    <h2 mat-dialog-title class="dialog-title">Build your avatar</h2>
    <mat-dialog-content class="dialog-content dialog-content-wide">
      <app-avatar-builder
        data-testid="avatar-builder"
        [initialConfig]="currentAvatarConfig"
        (saved)="onAvatarSaved($event)"
        (cancelled)="closeDialog()"
      >
      </app-avatar-builder>
    </mat-dialog-content>
  </ng-template>
</div>
